import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

export interface CartItem {
  id: string;
  type: 'song' | 'album' | 'video' | 'product';
  name: string;
  price: number;
  image: string;
  quantity: number;
  itemId: string;
}

interface PromoCode {
  code: string;
  discount: number;
  type: 'percentage' | 'fixed';
}

interface CartContextType {
  items: CartItem[];
  isOpen: boolean;
  promoCode: PromoCode | null;
  subtotal: number;
  tax: number;
  discount: number;
  total: number;
  addToCart: (item: Omit<CartItem, 'id' | 'quantity'>) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  toggleCart: () => void;
  applyPromoCode: (code: string) => Promise<boolean>;
  removePromoCode: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const TAX_RATE = 0.08;

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [promoCode, setPromoCode] = useState<PromoCode | null>(null);
  const [loading, setLoading] = useState(false);

  // Load cart from Supabase for logged-in users
  useEffect(() => {
    if (user) {
      loadCart();
    } else {
      loadCartFromLocalStorage();
    }
  }, [user]);

  // Save cart to Supabase or localStorage whenever it changes
  useEffect(() => {
    if (user) {
      saveCartToSupabase();
    } else {
      saveCartToLocalStorage();
    }
  }, [items, user]);

  const loadCart = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('cart_items')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;

      if (data) {
        const cartItems: CartItem[] = data.map((item) => ({
          id: item.id,
          type: item.type,
          name: item.name,
          price: item.price,
          image: item.image,
          quantity: item.quantity,
          itemId: item.item_id,
        }));
        setItems(cartItems);
      }
    } catch (error) {
      console.error('Error loading cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveCartToSupabase = async () => {
    if (!user || loading) return;

    try {
      // Delete all existing cart items for this user
      await supabase.from('cart_items').delete().eq('user_id', user.id);

      // Insert new cart items
      if (items.length > 0) {
        const cartData = items.map((item) => ({
          user_id: user.id,
          type: item.type,
          name: item.name,
          price: item.price,
          image: item.image,
          quantity: item.quantity,
          item_id: item.itemId,
        }));

        await supabase.from('cart_items').insert(cartData);
      }
    } catch (error) {
      console.error('Error saving cart:', error);
    }
  };

  const loadCartFromLocalStorage = () => {
    try {
      const savedCart = localStorage.getItem('visionmusic_cart');
      if (savedCart) {
        setItems(JSON.parse(savedCart));
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    }
  };

  const saveCartToLocalStorage = () => {
    try {
      localStorage.setItem('visionmusic_cart', JSON.stringify(items));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  };

  const addToCart = useCallback((item: Omit<CartItem, 'id' | 'quantity'>) => {
    setItems((prevItems) => {
      const existingItem = prevItems.find((i) => i.itemId === item.itemId && i.type === item.type);

      if (existingItem) {
        toast.success('Item quantity updated in cart');
        return prevItems.map((i) =>
          i.id === existingItem.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      } else {
        toast.success('Item added to cart');
        return [...prevItems, { ...item, id: crypto.randomUUID(), quantity: 1 }];
      }
    });
  }, []);

  const removeFromCart = useCallback((id: string) => {
    setItems((prevItems) => prevItems.filter((item) => item.id !== id));
    toast.success('Item removed from cart');
  }, []);

  const updateQuantity = useCallback((id: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(id);
      return;
    }

    setItems((prevItems) =>
      prevItems.map((item) => (item.id === id ? { ...item, quantity } : item))
    );
  }, [removeFromCart]);

  const clearCart = useCallback(() => {
    setItems([]);
    setPromoCode(null);
    toast.success('Cart cleared');
  }, []);

  const toggleCart = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const applyPromoCode = async (code: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('promo_codes')
        .select('*')
        .eq('code', code.toUpperCase())
        .eq('active', true)
        .single();

      if (error || !data) {
        toast.error('Invalid promo code');
        return false;
      }

      // Check if code is expired
      if (data.expires_at && new Date(data.expires_at) < new Date()) {
        toast.error('This promo code has expired');
        return false;
      }

      setPromoCode({
        code: data.code,
        discount: data.discount_value,
        type: data.discount_type,
      });

      toast.success(`Promo code "${data.code}" applied!`);
      return true;
    } catch (error) {
      console.error('Error applying promo code:', error);
      toast.error('Failed to apply promo code');
      return false;
    }
  };

  const removePromoCode = useCallback(() => {
    setPromoCode(null);
    toast.success('Promo code removed');
  }, []);

  // Calculate totals
  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const discount = promoCode
    ? promoCode.type === 'percentage'
      ? subtotal * (promoCode.discount / 100)
      : promoCode.discount
    : 0;

  const taxableAmount = subtotal - discount;
  const tax = taxableAmount * TAX_RATE;
  const total = taxableAmount + tax;

  const value: CartContextType = {
    items,
    isOpen,
    promoCode,
    subtotal,
    tax,
    discount,
    total,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    toggleCart,
    applyPromoCode,
    removePromoCode,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
